import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { SectionHeading, EmptyState, StatTile } from '../components/mailer/atoms';
import StateHeatmap from '../components/mailer/StateHeatmap';
import { useTheme } from '../components/layout/ThemeToggle';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mailer/dashboard`;

// Color-code response rate consistently with the dashboard:
//   ≥ 0.75%        green
//   0.50% – 0.74%  amber
//   < 0.50%        red
function respRateClass(v) {
  const n = Number(v || 0);
  if (n >= 0.75) return 'text-emerald-600';
  if (n >= 0.5) return 'text-amber-600';
  return 'text-red-600';
}

const COLS = [
  { key: 'state', label: 'State', align: 'left' },
  { key: 'total_mailed', label: 'Mailed' },
  { key: 'responses', label: 'Responded' },
  { key: 'submissions', label: 'Submitted' },
  { key: 'funded', label: 'Funded' },
  { key: 'response_rate', label: 'Resp %', suffix: '%' },
  { key: 'funded_rate', label: 'Funded %', suffix: '%' },
];

const METRIC_OPTIONS = [
  { value: 'total_mailed', label: 'Volume' },
  { value: 'responses', label: 'Responded' },
  { value: 'submissions', label: 'Submitted' },
  { value: 'funded', label: 'Funded' },
  { value: 'response_rate', label: 'Response %' },
];

function fmt(v, col) {
  if (v == null) return '—';
  if (col.suffix === '%') return `${Number(v).toFixed(col.key === 'response_rate' ? 2 : (v % 1 ? 1 : 0))}%`;
  if (typeof v === 'number') return v.toLocaleString('en-US');
  return String(v);
}

export default function MailerStates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('response_rate');
  const [sort, setSort] = useState({ key: 'total_mailed', dir: 'desc' });
  const [theme] = useTheme();

  useEffect(() => {
    axios.get(`${API}/states`).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
      return sort.dir === 'asc'
        ? String(av || '').localeCompare(String(bv || ''))
        : String(bv || '').localeCompare(String(av || ''));
    });
    return list;
  }, [rows, sort]);

  const totals = useMemo(() => ({
    states: rows.length,
    mailed: rows.reduce((s, r) => s + (r.total_mailed || 0), 0),
    submissions: rows.reduce((s, r) => s + (r.submissions || 0), 0),
    funded: rows.reduce((s, r) => s + (r.funded || 0), 0),
  }), [rows]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));

  if (loading) return <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"><div className="h-96 rounded-2xl border hairline bg-white animate-pulse" /></div>;
  if (rows.length === 0) return <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto"><EmptyState title="No state data yet" /></div>;

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto space-y-6" data-testid="mailer-states-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionHeading>Geographic Performance</SectionHeading>
        <select
          data-testid="mailer-states-metric"
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          className="h-9 px-3 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
        >
          {METRIC_OPTIONS.map((m) => <option key={m.value} value={m.value}>Color by · {m.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="States Covered" value={totals.states} testId="states-count" />
        <StatTile label="Pieces Mailed" value={totals.mailed} testId="states-mailed" />
        <StatTile label="Submitted" value={totals.submissions} testId="states-submissions" />
        <StatTile label="Funded" value={totals.funded} testId="states-funded" tone="positive" />
      </div>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="p-2">
          <StateHeatmap rows={rows} metric={metric} height={420} theme={theme === 'dark' ? 'dark' : 'light'} />
        </div>
      </div>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]" data-testid="mailer-states-table">
            <thead className="bg-neutral-50/60 border-b hairline">
              <tr>
                {COLS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleSort(col.key)}
                    className={cn(
                      'px-4 py-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold cursor-pointer select-none',
                      col.align === 'left' ? 'text-left' : 'text-right'
                    )}
                  >
                    <div className={cn('inline-flex items-center gap-1', col.align !== 'left' && 'justify-end w-full')}>
                      {col.label}
                      {sort.key === col.key
                        ? sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {sorted.map((r, i) => (
                <tr key={r.state} className="hover:bg-neutral-50/60" data-testid={`mailer-states-row-${i}`}>
                  {COLS.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        'px-4 py-3 align-middle',
                        col.align === 'left'
                          ? 'text-neutral-900 font-bold display tracking-tight'
                          : 'text-right display tracking-tight font-semibold',
                        col.key === 'response_rate'
                          ? respRateClass(r[col.key])
                          : col.align !== 'left' && 'text-neutral-800',
                      )}
                    >
                      {fmt(r[col.key], col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
